import propz from 'propz';
import { AppUser } from 'Src/views/App/App';

export interface GeneralMessagePreviewContext {
  recipientFirstName: string;
  recipientLastName: string;
  childFirstName: string;
  childLastName: string;
  childForm: string;
  childYearGroup: string;
  childHouse: string;
  schoolName: string;
  schoolLogoUrl: string;
  senderName: string;
  fromDisplayName: string;
}

const FAKE_CONTEXT: Omit<
  GeneralMessagePreviewContext,
  'schoolName' | 'schoolLogoUrl' | 'senderName' | 'fromDisplayName'
> = {
  recipientFirstName: 'Sarah',
  recipientLastName: 'Thompson',
  childFirstName: 'Oliver',
  childLastName: 'Thompson',
  childForm: '8H',
  childYearGroup: '8',
  childHouse: 'Platinum'
};

export function buildPreviewContext(user: AppUser, senderDisplayName: string): GeneralMessagePreviewContext {
  const school = propz.get(user, ['activeSchool'], {});
  const schoolName = propz.get(school, ['name'], 'Your School');
  const schoolLogo =
    propz.get(school, ['pic'], '') || propz.get(school, ['logoUrl'], '') || propz.get(school, ['logo'], '');

  const rawSenderName =
    (
      `${propz.get(user, ['activePermission', 'firstName'], '')} ${propz.get(
        user,
        ['activePermission', 'lastName'],
        ''
      )}`.trim() ||
      `${propz.get(user, ['profile', 'firstName'], '')} ${propz.get(user, ['profile', 'lastName'], '')}`.trim() ||
      propz.get(user, ['activeSchoolName'], '')
    ).trim() || 'Sender';
  const fromDisplayName = senderDisplayName
    ? senderDisplayName.replace(/\{\{schoolName\}\}/g, schoolName).replace(/\{\{senderName\}\}/g, rawSenderName)
    : rawSenderName;

  return {
    ...FAKE_CONTEXT,
    schoolName,
    schoolLogoUrl: schoolLogo,
    senderName: rawSenderName,
    fromDisplayName
  };
}

function getSchoolLogoHtml(url: string): string {
  if (!url) return '';
  return `<img src="${url}" alt="School logo" style="max-height:80px;" />`;
}

function resolveText(text: string, ctx: GeneralMessagePreviewContext): string {
  if (!text) return text;

  const now = new Date();
  const currentDate = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const currentYear = String(now.getFullYear());

  return text
    .replace(/\{\{childFirstName\}\}/g, ctx.childFirstName)
    .replace(/\{\{childLastName\}\}/g, ctx.childLastName)
    .replace(/\{\{childFullName\}\}/g, `${ctx.childFirstName} ${ctx.childLastName}`)
    .replace(/\{\{childForm\}\}/g, ctx.childForm)
    .replace(/\{\{childYearGroup\}\}/g, ctx.childYearGroup)
    .replace(/\{\{childHouse\}\}/g, ctx.childHouse)
    .replace(/\{\{recipientFirstName\}\}/g, ctx.recipientFirstName)
    .replace(/\{\{recipientLastName\}\}/g, ctx.recipientLastName)
    .replace(/\{\{recipientFullName\}\}/g, `${ctx.recipientFirstName} ${ctx.recipientLastName}`)
    .replace(/\{\{schoolName\}\}/g, ctx.schoolName)
    .replace(/\{\{schoolLogo\}\}/g, getSchoolLogoHtml(ctx.schoolLogoUrl))
    .replace(/\{\{senderName\}\}/g, ctx.senderName)
    .replace(/\{\{currentYear\}\}/g, currentYear)
    .replace(/\{\{currentDate\}\}/g, currentDate);
}

export interface GeneralMessagePreviewResult {
  emailSubject: string;
  emailBody: string;
  pushTitle: string;
  pushBody: string;
  smsText: string;
}

export function resolveGeneralMessagePreview(
  emailSubject: string,
  emailBody: string,
  pushTitle: string,
  pushBody: string,
  smsText: string,
  ctx: GeneralMessagePreviewContext
): GeneralMessagePreviewResult {
  return {
    emailSubject: resolveText(emailSubject, ctx),
    emailBody: resolveText(emailBody, ctx),
    pushTitle: resolveText(pushTitle, ctx),
    pushBody: resolveText(pushBody, ctx),
    smsText: resolveText(smsText, ctx)
  };
}

